import { Injectable, inject } from '@angular/core';
import { Capacitor, PluginListenerHandle, registerPlugin } from '@capacitor/core';
import { TranslationService } from 'src/app/i18n/translation.service';
import { SharedFilesService } from './shared-files.service';
import { ToastMessagesService } from './toast-messages.service';

/** One file the native side copied into the app cache (see ShareReceiverPlugin.java). */
interface NativeFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  path: string;
}

interface NativeFilesResult {
  files: NativeFile[];
  /** Files that could not be read (too big, sender revoked access, unsupported link...). */
  failed: number;
  /** Clipboard only: the clipboard held text but no file. */
  textOnly: boolean;
}

interface ShareReceiverPlugin {
  getSharedFiles(): Promise<NativeFilesResult>;
  readClipboardFiles(): Promise<NativeFilesResult>;
  release(options: { ids: string[] }): Promise<void>;
  addListener(event: 'sharedFilesReceived', cb: () => void): Promise<PluginListenerHandle>;
}

const ShareReceiver = registerPlugin<ShareReceiverPlugin>('ShareReceiver');

/**
 * Web side of the native "Share to LumberLinq" / "Paste files" plugin (Android now; iPhone once its plugin exists).
 *
 * - While the logged-in shell is mounted (start()/stop(), driven by the sheet host), files shared from another app
 *   are collected and put on hold with SharedFilesService, which opens the "Where should these files go?" sheet.
 *   Files shared while logged out simply wait natively (12 h) until the next login.
 * - readClipboard() backs the "Paste" item in the upload button's phone menu.
 *
 * Nothing is uploaded here.
 */
@Injectable({ providedIn: 'root' })
export class NativeShareService {
  private readonly shared = inject(SharedFilesService);
  private readonly toast = inject(ToastMessagesService);
  private readonly i18n = inject(TranslationService);

  private running = false;
  private listener?: PluginListenerHandle;
  private draining = false;
  private drainAgain = false;

  /** True only inside the native app where the ShareReceiver plugin actually exists. */
  get available(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ShareReceiver');
  }

  async start(): Promise<void> {
    if (!this.available || this.running) return;
    this.running = true;
    try {
      this.listener = await ShareReceiver.addListener('sharedFilesReceived', () => void this.drain());
    } catch {
      /* the initial drain below still picks up anything already waiting */
    }
    await this.drain();
  }

  stop(): void {
    this.running = false;
    void this.listener?.remove();
    this.listener = undefined;
  }

  /** Read whatever is on the phone clipboard as real Files (none if it only holds text or nothing). */
  async readClipboard(): Promise<{ files: File[]; textOnly: boolean }> {
    if (!this.available) return { files: [], textOnly: false };
    const res = await ShareReceiver.readClipboardFiles();
    const files = await this.toFiles(res.files);
    if (res.failed > 0 && files.length === 0) this.warnFailed();
    return { files, textOnly: !!res.textOnly && files.length === 0 };
  }

  /** Collect shared files and put them on hold. Runs one at a time; a ping during a run triggers one more pass. */
  private async drain(): Promise<void> {
    if (this.draining) { this.drainAgain = true; return; }
    this.draining = true;
    try {
      do {
        this.drainAgain = false;
        const res = await ShareReceiver.getSharedFiles();
        const files = await this.toFiles(res.files);
        // Logged out while we were reading: drop them (the copies are already released) instead of showing a
        // sheet to whoever logs in next.
        if (!this.running) continue;
        if (files.length) this.shared.offer(files);
        if (res.failed > 0) this.warnFailed();
      } while (this.drainAgain && this.running);
    } catch {
      /* plugin unavailable / bridge closing — nothing to do */
    } finally {
      this.draining = false;
    }
  }

  /** Cache file -> File in memory, then delete the cache copy. Unreadable ones are skipped (caller sees `failed`). */
  private async toFiles(list: NativeFile[]): Promise<File[]> {
    const out: File[] = [];
    for (const f of list ?? []) {
      try {
        const res = await fetch(Capacitor.convertFileSrc('file://' + f.path));
        if (!res.ok) throw new Error('read failed');
        const blob = await res.blob();
        // lastModified is fixed so sharing the same file twice while the first is still waiting is de-duplicated.
        out.push(new File([blob], f.name, { type: f.mimeType || blob.type, lastModified: 0 }));
      } catch {
        /* skipped */
      }
    }
    if (list?.length) {
      try { await ShareReceiver.release({ ids: list.map(f => f.id) }); } catch { /* purged after 12 h anyway */ }
    }
    return out;
  }

  private warnFailed(): void {
    this.toast.showWarningMessage(
      this.i18n.instant('sfs.native.failedTitle'),
      this.i18n.instant('sfs.native.failed'),
    );
  }
}
